import React from 'react';
import { observer } from 'mobx-react';
import { CircularProgress } from '@ann/web-components';
import { ContentArea, LoadingContainer } from '../../DriveWorkspace.styled';
import EmptyWorkspaceState from '../EmptyWorkspaceState';
import UnifiedTable from '../../../../components/UnifiedTable';
import FoldersList from '../../../MyWorkspace/components/FoldersList';
import DocumentsList from '../../../MyWorkspace/components/DocumentsList';

/**
 * Component for rendering the main content area of DriveWorkspace
 * Handles loading, empty, table, and grid views
 */
const DriveWorkspaceContent = observer(({
  // Loading state
  isLoading,
  
  // Empty state props
  isEmpty,
  vaultUuid,
  openFileSelector,
  handleDrop,
  
  // View mode
  viewMode,
  
  // Data
  visibleFolders,
  visibleDocuments,
  
  // Store
  selectionStore,
  openDuplicateModal,
  openMoveModal,
  
  // Pagination - Folders
  hasMoreFolders,
  isLoadingFolders,
  loadMoreFolders,
  
  // Pagination - Documents
  hasMoreDocuments,
  isLoadingDocuments,
  loadMoreDocuments,
  
  // Action handlers
  onShare,
  onAssignAgent,
  onRenameFolder,
  onRenameDocument,
  onFolderInfo,
  onDelete,
  onOpenDocumentDetail,
}) => {
  // Loading state
  if (isLoading) {
    return (
      <ContentArea>
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      </ContentArea>
    );
  }

  // Empty state (only in grid view)
  const showEmptyState = isEmpty && viewMode === 'grid';
  if (showEmptyState) {
    return (
      <ContentArea>
        <EmptyWorkspaceState
          vaultUuid={vaultUuid}
          openFileSelector={openFileSelector}
          handleDrop={handleDrop}
        />
      </ContentArea>
    );
  }

  // Table view
  if (viewMode === 'table') {
    return (
      <UnifiedTable
        folders={visibleFolders}
        documents={visibleDocuments}
        selectionStore={selectionStore}
        onShare={onShare}
        onDuplicate={openDuplicateModal}
        onAssignAgent={onAssignAgent}
        onRenameFolder={onRenameFolder}
        onRenameDocument={onRenameDocument}
        onFolderInfo={onFolderInfo}
        onMove={(itemId, type) => openMoveModal(itemId, type)}
        onDelete={onDelete}
        hasMore={hasMoreDocuments}
        isLoading={isLoadingDocuments}
        onLoadMore={loadMoreDocuments}
      />
    );
  }

  // Grid view (default)
  return (
    <ContentArea>
      <FoldersList
        folders={visibleFolders}
        selectionStore={selectionStore}
        onShare={onShare}
        onRename={onRenameFolder}
        onDuplicate={openDuplicateModal}
        onMove={(folderId) => openMoveModal(folderId, 'folder')}
        onFolderInfo={onFolderInfo}
        onAssignAgent={onAssignAgent}
        hasMore={hasMoreFolders}
        isLoading={isLoadingFolders}
        onLoadMore={loadMoreFolders}
      />

      <DocumentsList
        documents={visibleDocuments}
        selectionStore={selectionStore}
        onShare={onShare}
        onDuplicate={openDuplicateModal}
        onRename={onRenameDocument}
        onMove={(documentId) => openMoveModal(documentId, 'document')}
        onDelete={onDelete}
        onAssignAgent={onAssignAgent}
        onOpenDocumentDetail={onOpenDocumentDetail}
        hasMore={hasMoreDocuments}
        isLoading={isLoadingDocuments}
        onLoadMore={loadMoreDocuments}
        onDropFiles={handleDrop}
        hiddenActions={{
          download: true,
          rename: false,
          share: false,
          assignAgent: false,
          duplicate: false,
          move: false,
          delete: false
        }}
      />
    </ContentArea>
  );
});

DriveWorkspaceContent.displayName = 'DriveWorkspaceContent';

export default DriveWorkspaceContent;
